import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Plus } from "lucide-react";
import { useVault } from "@/context/VaultContext";
import { contactsMap, nameOf } from "@/lib/selectors";
import { StoryComposer } from "@/components/aph/StoryComposer";
import { StoryViewer } from "@/components/aph/StoryViewer";

function Ring({ label, unseen, onClick, testId, children }) {
  return (
    <button onClick={onClick} data-testid={testId} className="flex flex-col items-center gap-1.5 w-16 flex-shrink-0">
      <span className={`w-14 h-14 rounded-full p-[2px] ${unseen ? "bg-stellar shadow-stellar-glow" : "bg-rim"}`}>
        <span className="w-full h-full rounded-full bg-void flex items-center justify-center font-branding font-bold text-stellar">{children}</span>
      </span>
      <span className="text-[10px] text-ash/60 truncate w-full text-center">{label}</span>
    </button>
  );
}

export function StoriesBar() {
  const { q, version, profile } = useVault();
  const [composer, setComposer] = useState(false);
  const [viewing, setViewing] = useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const contacts = useMemo(() => contactsMap(q), [version]);
  const groups = useMemo(() => {
    const rows = q("SELECT * FROM stories WHERE expires_at>? ORDER BY timestamp ASC", [Date.now()]).map((s) => ({ ...s, content: JSON.parse(s.content) }));
    const by = {};
    for (const s of rows) (by[s.author_code] ||= []).push(s);
    return by;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);
  const mine = groups[profile.code] || [];
  const others = Object.entries(groups).filter(([a]) => a !== profile.code)
    .sort(([, a], [, b]) => (a.some((s) => !s.seen) === b.some((s) => !s.seen) ? 0 : a.some((s) => !s.seen) ? -1 : 1));

  return (
    <div className="px-3 py-3 border-b border-glass flex gap-2 overflow-x-auto" data-testid="stories-bar">
      <div className="relative">
        <Ring label="Your story" unseen={false} testId="story-mine-btn" onClick={() => (mine.length ? setViewing(profile.code) : setComposer(true))}>{profile.alias.slice(0, 1).toUpperCase()}</Ring>
        <button onClick={() => setComposer(true)} data-testid="story-add-btn" className="absolute top-9 right-0.5 w-5 h-5 rounded-full bg-stellar text-void flex items-center justify-center border-2 border-void"><Plus size={12} strokeWidth={2.5} /></button>
      </div>
      {others.map(([author, list]) => (
        <Ring key={author} label={nameOf(contacts[author])} unseen={list.some((s) => !s.seen)} testId={`story-ring-${author.slice(4, 14)}`} onClick={() => setViewing(author)}>
          {nameOf(contacts[author]).slice(0, 1).toUpperCase()}
        </Ring>
      ))}
      <StoryComposer open={composer} onOpenChange={setComposer} />
      {viewing && groups[viewing] && createPortal(<StoryViewer author={viewing} stories={groups[viewing]} contacts={contacts} onClose={() => setViewing(null)} />, document.body)}
    </div>
  );
}
